import { getToken } from 'firebase/messaging';
import { doc, setDoc } from 'firebase/firestore';
import { db, getMessagingIfSupported } from './firebaseConfig';

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

// False in an iOS Safari tab, true in an installed iOS PWA on 16.4+. Drives
// whether we promise a push or a text when someone joins the waitlist.
export const isPushSupported = async () => (await getMessagingIfSupported()) !== null;

// An iPhone can only receive push once the app is on the home screen, so a
// Safari tab needs a nudge rather than a permission prompt it can't fulfil.
export const isUninstalledIos = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.navigator.standalone;

// Tokens live at users/{uid}/pushTokens/{token} - the doc id is the token, so
// re-registering the same device is idempotent and two devices never race.
const storeToken = (uid, token) =>
  setDoc(
    doc(db, 'users', uid, 'pushTokens', token),
    {
      token,
      createdAt: new Date(),
      lastSeenAt: new Date(),
      userAgent: navigator.userAgent
    },
    { merge: true }
  );

// Shared by both entry points below. Assumes permission is already granted.
const registerToken = async (uid, messaging) => {
  const serviceWorkerRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
  const token = await getToken(messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration });

  if (!token) return { ok: false, reason: 'error' };

  await storeToken(uid, token);
  return { ok: true };
};

// Prompting path, called when a parent joins a waitlist. Never throws - joining
// the waitlist has already committed and must not be undone by a failure here.
export const registerForPush = async (uid) => {
  try {
    const messaging = await getMessagingIfSupported();
    if (!messaging) return { ok: false, reason: 'unsupported' };
    if (Notification.permission === 'denied') return { ok: false, reason: 'denied' };

    if (Notification.permission !== 'granted') {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') return { ok: false, reason: 'denied' };
    }

    return await registerToken(uid, messaging);
  } catch (error) {
    console.error('Error registering for push notifications:', error);
    return { ok: false, reason: 'error' };
  }
};

// Silent path, called on every sign-in. FCM rotates tokens, so a household that
// opted in months ago goes quietly dark without this. Returns early unless
// permission is already granted, so it can never surface a prompt.
export const refreshPushToken = async (uid) => {
  try {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;

    const messaging = await getMessagingIfSupported();
    if (!messaging) return;

    await registerToken(uid, messaging);
  } catch (error) {
    console.error('Error refreshing push token:', error);
  }
};
